import { Suspense } from 'react';

import { flattenEntry, getEntries } from '@/lib/cms';

import { LimitPagination, TopicEntry } from '@/components/cms/types/entry';

import { Feed } from './Feed';

type ArticlesFeedProps = {
  initialTopicSlug?: string;
  paginationLabel: string;
  hasFilters: boolean;
  hasSearch: boolean;
  featuredTopics: TopicEntry[];
};

export const ARTICLES_PER_PAGE = 6;

export async function ArticlesFeed({
  initialTopicSlug,
  hasFilters,
  hasSearch,
  paginationLabel,
  featuredTopics,
}: ArticlesFeedProps) {
  const initialArticles = await getArticles(initialTopicSlug);
  const allTopics = await getAllTopics();

  return (
    <Suspense>
      <div data-tracker-section='blog-articles'>
        <Feed
          initialArticles={initialArticles}
          allTopics={allTopics}
          featuredTopics={featuredTopics}
          initialTopicSlug={initialTopicSlug}
          paginationLabel={paginationLabel}
          hasFilters={hasFilters}
          hasSearch={hasSearch}
        />
      </div>
    </Suspense>
  );
}

async function getArticles(topicSlug?: string) {
  const entries = await getEntries({
    contentType: '/blogs',
    query: {
      pLevel: 'deep',
      sort: ['metadata.releaseDate:desc', 'createdAt:desc'],
      filters: {
        metadata: {
          ...(!!topicSlug && { topics: { slug: { $containsi: topicSlug } } }),
        },
      },
      pagination: {
        start: 0,
        limit: ARTICLES_PER_PAGE,
      },
    },
  });

  if (!entries?.data) return;

  return {
    data: entries.data.map(flattenEntry),
    pagination: (entries.meta?.pagination ?? {}) as LimitPagination,
  };
}

async function getAllTopics() {
  const entries = await getEntries<TopicEntry>({
    contentType: '/blog-topics',
    query: {
      sort: 'topic:asc',
      pagination: {
        start: 0,
        limit: 50,
      },
    },
  });

  if (!entries?.data) return;

  return {
    data: entries.data.map(flattenEntry),
    pagination: (entries.meta?.pagination ?? {}) as LimitPagination,
  };
}
